<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Suscripción</h1>
          <p class="view-header__subtitle">{{ headerSubtitle }}</p>
        </div>
      </div>
    </div>

    <!-- ESTADO -->
    <div v-if="user && user.OWNER != 1" class="alert alert-danger d-flex align-items-start mb-3">
      <i class="bi bi-shield-lock-fill mr-2 mt-1"></i>
      <div>
        Solo el dueño de la cuenta puede pagar la suscripción. Contactá al dueño para que realice el pago.
      </div>
    </div>

    <div
      v-else-if="status"
      class="alert d-flex align-items-start mb-3"
      :class="`alert-${status.variant}`"
    >
      <i class="mr-2 mt-1" :class="status.icon"></i>
      <div>{{ status.text }}</div>
    </div>

    <div v-if="user && user.OWNER == 1" class="row d-flex justify-content-center">
      <!-- PLAN / RENOVACIÓN -->
      <div class="col-12 col-md-6">
        <div class="form-card">
          <div class="form-card__header">
            <h6 class="form-card__title">
              <i class="bi bi-credit-card-2-front"></i>
              Renovar Qupro
            </h6>
          </div>
          <div class="form-card__body">
            <div class="billing-plan">
              <div class="billing-plan__price">{{ formatoDinamicoMoneda(price) }}</div>
              <div class="billing-plan__period">por un mes de Qupro</div>
            </div>

            <div class="billing-plan__meta">
              <div class="billing-plan__row">
                <span class="billing-plan__label">Fecha actual</span>
                <span>{{ company.today || '—' }}</span>
              </div>
              <div class="billing-plan__row">
                <span class="billing-plan__label">Vence el</span>
                <span>{{ company.expdate_human || '—' }}</span>
              </div>
              <div class="billing-plan__row">
                <span class="billing-plan__label">Se extendería hasta</span>
                <span class="data-table__primary">{{ company.expdate_extended_human || '—' }}</span>
              </div>
            </div>
          </div>
          <div class="form-card__footer">
            <a
              v-if="url"
              id="promo"
              :href="url"
              mp-mode="modal"
              name="MP-Checkout"
              class="form-card__btn form-card__btn--primary billing-plan__cta"
            >
              <i class="bi bi-credit-card"></i>
              Pagar con MercadoPago
            </a>
            <span v-else class="form-card__hint">No se pudo generar el link de pago. Refrescá la página o intentá de nuevo en unos minutos.</span>
          </div>
        </div>
      </div>

      <!-- HISTORIAL -->
      <div class="col-12">
        <div class="form-card">
          <div class="form-card__header">
            <h6 class="form-card__title">
              <i class="bi bi-receipt"></i>
              Pagos registrados
            </h6>
          </div>
          <div class="data-table billing-payments__table">
            <div class="table-responsive">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Descripción</th>
                    <th class="text-right">Monto</th>
                    <th>Extensión hasta</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!payments.length">
                    <td colspan="4" class="data-table__empty">Aún no registraste pagos.</td>
                  </tr>
                  <tr v-for="payment in paginatedPayments" :key="payment.id">
                    <td>{{ payment.created_at }}</td>
                    <td class="data-table__muted">{{ payment.description }}</td>
                    <td class="text-right">
                      <span class="data-table__primary">{{ formatoDinamicoMoneda(payment.amount) }}</span>
                    </td>
                    <td>{{ payment.todate }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="payments.length" class="data-table__footer">
              <div class="data-table__info">
                Mostrando {{ pageStart }}–{{ pageEnd }} de {{ payments.length }}
              </div>
              <ul class="data-table__pagination">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a href="#" class="page-link" @click.prevent="goToPage(currentPage - 1)">
                    <i class="bi bi-chevron-left"></i>
                  </a>
                </li>
                <li
                  v-for="p in pageNumbers"
                  :key="p"
                  class="page-item"
                  :class="{ active: p === currentPage }"
                >
                  <a href="#" class="page-link" @click.prevent="goToPage(p)">{{ p }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <a href="#" class="page-link" @click.prevent="goToPage(currentPage + 1)">
                    <i class="bi bi-chevron-right"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import formatMoneyMixin from "../mixins/formatMoneyMixin";

export default {
  mixins: [formatMoneyMixin],
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
    };
  },
  computed: {
    ...mapGetters({
      user: 'user/showUser',
      company: 'billing/showCompany',
      payments: 'billing/showPayments',
      url: 'billing/showUrl',
      price: 'billing/showPrice',
    }),
    ...mapState('billing', { isLoading: state => state.loading }),
    headerSubtitle() {
      if (!this.company) return 'Estado de tu suscripción y pagos.';
      if (this.company.expired) return `Tu ${this.company.trial == 1 ? 'período de prueba' : 'suscripción'} venció el ${this.company.expdate_human}.`;
      if (this.company.last_week) return `Tu suscripción vence pronto (${this.company.expdate_human}).`;
      if (this.company.expdate_human) return `Suscripción activa hasta el ${this.company.expdate_human}.`;
      return 'Estado de tu suscripción y pagos.';
    },
    status() {
      if (!this.company) return null;
      if (this.company.expired) {
        const tipo = this.company.trial == 1 ? 'período de prueba' : 'suscripción';
        return {
          variant: 'danger',
          icon: 'bi bi-exclamation-octagon-fill',
          text: `Tu ${tipo} expiró el ${this.company.expdate_human}. Renová para seguir usando Qupro.`,
        };
      }
      if (this.company.last_week) {
        return {
          variant: 'warning',
          icon: 'bi bi-exclamation-triangle-fill',
          text: `Tu suscripción vence en menos de una semana. Te recomendamos renovar antes del ${this.company.expdate_human}.`,
        };
      }
      return null;
    },
    totalPages() {
      return Math.max(1, Math.ceil((this.payments.length || 0) / this.pageSize));
    },
    pageStart() {
      if (!this.payments.length) return 0;
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.payments.length);
    },
    paginatedPayments() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.payments.slice(start, start + this.pageSize);
    },
    pageNumbers() {
      // Ventana acotada: hasta 5 páginas alrededor de la actual.
      const total = this.totalPages;
      const max = 5;
      let from = Math.max(1, this.currentPage - Math.floor(max / 2));
      let to = Math.min(total, from + max - 1);
      from = Math.max(1, to - max + 1);
      return Array.from({ length: to - from + 1 }, (_, i) => from + i);
    },
  },
  watch: {
    'payments.length'() {
      // Si cambia la cantidad de pagos (recarga del store), volvé a página 1.
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions({
      getBilling: 'billing/getBilling',
    }),
    goToPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.currentPage = p;
    },
  },
  mounted() {
    this.getBilling();
  },
};
</script>

<style scoped>
.billing-plan {
  text-align: center;
  padding: 8px 0 16px;
  border-bottom: 1px solid var(--border-soft);
  margin-bottom: 16px;
}

.billing-plan__price {
  font-size: 32px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.1;
}

.billing-plan__period {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 4px;
}

.billing-plan__meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.billing-plan__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: var(--text-soft);
}

.billing-plan__label {
  color: var(--text-muted);
  font-weight: 500;
}

.billing-plan__cta {
  flex: 1;
  justify-content: center;
}

.billing-payments__table {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  margin-bottom: 0;
}
</style>
